type ModalType = 'modal' | 'slideOver' | 'dialog';

interface ModalInfo {
  id: string;
  type: ModalType;
  preventScroll: boolean;
}

class ModalManager {
  private openModals = new Map<string, ModalInfo>();
  private originalOverflow = '';

  /**
   * Register a modal as opened
   */
  openModal(id: string, type: ModalType = 'modal', preventScroll = true) {
    // Store original overflow only when first modal opens
    if (this.openModals.size === 0) {
      this.originalOverflow = document.body.style.overflow || '';
    }

    this.openModals.set(id, { id, type, preventScroll });

    // Apply scroll prevention if any open modal requires it
    const shouldPreventScroll = Array.from(this.openModals.values()).some(
      (modal) => modal.preventScroll,
    );

    if (shouldPreventScroll) {
      document.body.style.overflow = 'hidden';
    }
  }

  /**
   * Unregister a modal as closed
   */
  closeModal(id: string) {
    const wasOpen = this.openModals.has(id);
    this.openModals.delete(id);

    if (!wasOpen) {
      console.warn(
        `Attempted to close modal "${id}" that was not registered as open`,
      );
      return;
    }

    // If no modals are open, restore original scroll behavior
    if (this.openModals.size === 0) {
      document.body.style.overflow = this.originalOverflow;
      this.originalOverflow = '';
    } else {
      // Check if remaining modals need scroll prevention
      const shouldPreventScroll = Array.from(this.openModals.values()).some(
        (modal) => modal.preventScroll,
      );

      if (!shouldPreventScroll) {
        document.body.style.overflow = this.originalOverflow;
      }
    }
  }

  /**
   * Get list of currently open modals
   */
  getOpenModals(): ModalInfo[] {
    return Array.from(this.openModals.values());
  }

  /**
   * Check if a specific modal is open
   */
  isModalOpen(id: string): boolean {
    return this.openModals.has(id);
  }

  /**
   * Get count of open modals
   */
  getOpenModalCount(): number {
    return this.openModals.size;
  }

  /**
   * Get count of open modals by type
   */
  getOpenModalCountByType(type: ModalType): number {
    return Array.from(this.openModals.values()).filter(
      (modal) => modal.type === type,
    ).length;
  }

  /**
   * Force close all modals (emergency cleanup)
   */
  closeAllModals() {
    console.warn('Force closing all modals');
    this.openModals.clear();
    document.body.style.overflow = this.originalOverflow;
    this.originalOverflow = '';
  }
}

// Create singleton instance
export const modalManager = new ModalManager();

// Export hook for React components
export const useModalManager = () => modalManager;
